/**
 * ClassificationScene - Specimen classification challenges
 * Players scan specimens and classify them based on evidence
 */

import Phaser from 'phaser';
import { MicroscopeSystem, ScannerSystem, ClassificationSystem } from '@/game/systems';
import type { Specimen, ScanCompleteEvent, ClassificationResultEvent } from '@/game/systems';
import { MicroscopeHUD, ScannerPanel, EvidenceCards, ClassificationBins } from '@/ui/components';
import { CellAnimationSystem, NonLivingGenerator } from '@/procedural/graphics';
import { getLevel, getChapterForLevel } from '@/data';
import { getOrganismTemplate, getNonLivingTemplate, isLivingSpecimen } from '@/data';
import { markLevelComplete } from './MenuScene';
import type { Level, LevelObjective, EvidenceCard, OrganismTemplate } from '@/types';

export interface ClassificationSceneConfig {
  levelId: string;
}

interface ObjectiveProgress {
  objective: LevelObjective;
  current: number;
  target: number;
  complete: boolean;
}

interface SpecimenEntry {
  id: string;
  templateId: string;
  specimen: Specimen;
  container: Phaser.GameObjects.Container;
  classified: boolean;
  correct: boolean;
}

/**
 * ClassificationScene presents specimens for classification
 * - One specimen at a time
 * - Evidence-based classification
 * - Accuracy tracking
 */
export class ClassificationScene extends Phaser.Scene {
  private levelId!: string;
  private level!: Level;

  /** Systems */
  private microscopeSystem!: MicroscopeSystem;
  private scannerSystem!: ScannerSystem;
  private classificationSystem!: ClassificationSystem;
  private animationSystem!: CellAnimationSystem;
  private nonLivingGenerator!: NonLivingGenerator;

  /** UI Components */
  private microscopeHUD!: MicroscopeHUD;
  private scannerPanel!: ScannerPanel;
  private evidenceCards!: EvidenceCards;
  private classificationBins!: ClassificationBins;

  /** Game objects */
  private slideContainer!: Phaser.GameObjects.Container;
  private specimenQueue: SpecimenEntry[] = [];
  private currentSpecimenIndex = 0;
  private currentEntry: SpecimenEntry | null = null;

  /** Game state */
  private objectives: ObjectiveProgress[] = [];
  private correctCount = 0;
  private totalAttempts = 0;
  private collectedEvidence: EvidenceCard[] = [];

  /** UI elements */
  private progressIndicator!: Phaser.GameObjects.Container;
  private accuracyText!: Phaser.GameObjects.Text;
  private completionOverlay?: Phaser.GameObjects.Container;

  constructor() {
    super({ key: 'ClassificationScene' });
  }

  init(data: ClassificationSceneConfig): void {
    this.levelId = data.levelId;

    // Reset state
    this.specimenQueue = [];
    this.currentSpecimenIndex = 0;
    this.currentEntry = null;
    this.objectives = [];
    this.correctCount = 0;
    this.totalAttempts = 0;
    this.collectedEvidence = [];
  }

  create(): void {
    // Load level data
    const level = getLevel(this.levelId);
    if (!level) {
      console.error(`Level not found: ${this.levelId}`);
      this.scene.start('MenuScene');
      return;
    }
    this.level = level;

    // Create slide background
    this.createSlideBackground();

    // Initialize systems
    this.initializeSystems();

    // Prepare specimens
    this.prepareSpecimens();

    // Create UI
    this.createUI();

    // Initialize objectives
    this.initializeObjectives();

    // Setup event listeners
    this.setupEventListeners();

    // Start with first specimen
    this.showNextSpecimen();

    // Emit level start event
    this.events.emit('level:start', { levelId: this.levelId, level: this.level });
  }

  override update(_time: number, delta: number): void {
    this.animationSystem.update(delta);
  }

  /**
   * Create the microscope slide background
   */
  private createSlideBackground(): void {
    const { width, height } = this.cameras.main;

    this.slideContainer = this.add.container(width / 2, height / 2 - 50);

    // Slide background
    const slideBg = this.add.graphics();
    slideBg.fillStyle(0xfef5f5, 1);
    slideBg.fillRect(-300, -250, 600, 500);

    // Border
    slideBg.lineStyle(3, 0x1a365d, 0.8);
    slideBg.strokeRect(-300, -250, 600, 500);

    // Subtle grid pattern
    slideBg.lineStyle(1, 0x000000, 0.03);
    for (let i = -300; i <= 300; i += 50) {
      slideBg.lineBetween(i, -250, i, 250);
    }
    for (let i = -250; i <= 250; i += 50) {
      slideBg.lineBetween(-300, i, 300, i);
    }

    // Microscope field outline (circular)
    const fieldMask = this.add.graphics();
    fieldMask.fillStyle(0x000000, 0.1);
    fieldMask.fillCircle(0, 0, 220);
    fieldMask.lineStyle(2, 0x4299e1, 0.3);
    fieldMask.strokeCircle(0, 0, 220);

    this.slideContainer.add([slideBg, fieldMask]);
  }

  /**
   * Initialize game systems
   */
  private initializeSystems(): void {
    // Microscope system
    this.microscopeSystem = new MicroscopeSystem(this, {
      initialZoomIndex: 3, // Start at 100x
      touchEnabled: true,
    });

    // Scanner system
    this.scannerSystem = new ScannerSystem(this, {
      scanDuration: 2500,
      scanRange: 100,
    });

    // Classification system
    this.classificationSystem = new ClassificationSystem(this, {
      minEvidence: 3,
      expertMinEvidence: 5,
      expertMode: false,
    });

    // Animation system
    this.animationSystem = new CellAnimationSystem(this, {
      pulseAmplitude: 0.03,
      pulseSpeed: 0.4,
      driftRadius: 3,
      driftSpeed: 0.3,
      cytoplasmFlow: true,
      speedMultiplier: 1,
    });
    this.animationSystem.start();

    // Non-living generator
    this.nonLivingGenerator = new NonLivingGenerator(this, Date.now());
  }

  /**
   * Prepare specimen queue from level data
   */
  private prepareSpecimens(): void {
    if (!this.level.specimens) return;

    // Shuffle specimens for variety
    const shuffled = [...this.level.specimens].sort(() => Math.random() - 0.5);

    shuffled.forEach((templateId, index) => {
      const entry = this.createSpecimenEntry(templateId, index);
      if (entry) {
        this.specimenQueue.push(entry);
      }
    });
  }

  /**
   * Create a specimen entry
   */
  private createSpecimenEntry(templateId: string, index: number): SpecimenEntry | null {
    const isLiving = isLivingSpecimen(templateId);
    const id = `${templateId}_${index}`;

    let container: Phaser.GameObjects.Container;
    let size: number;

    if (isLiving) {
      const template = getOrganismTemplate(templateId);
      if (!template) {
        console.warn(`Unknown organism template: ${templateId}`);
        return null;
      }

      size = (template.cellConfig.size.min + template.cellConfig.size.max) / 2;

      // Create animated cell
      const cellId = `cell_${id}`;
      this.animationSystem.createAnimatedCell(template.cellConfig, Date.now() + index, cellId);

      const animatedCell = this.animationSystem.getCell(cellId);
      container = animatedCell?.result.container ?? this.add.container(0, 0);
      container.setVisible(false);

      // Determine cell count from template type
      const cellCount = template.type === 'unicellular' ? 'single' : 'multiple';

      // Build specimen data
      const specimen: Specimen = {
        id,
        position: { x: 0, y: 0 },
        isLiving: true,
        cellCount,
        cellType: this.getCellTypeFromTemplate(template),
        lifeFunctions: [],
        bounds: new Phaser.Geom.Rectangle(-size / 2, -size / 2, size, size),
      };

      return {
        id,
        templateId,
        specimen,
        container,
        classified: false,
        correct: false,
      };
    } else {
      const template = getNonLivingTemplate(templateId);
      if (!template) {
        console.warn(`Unknown non-living template: ${templateId}`);
        return null;
      }

      size = 60;

      const rendered = this.nonLivingGenerator.generate(template, {
        size,
        seed: Date.now() + index,
      });
      container = rendered.container;
      container.setVisible(false);

      this.slideContainer.add(container);

      // Build specimen data
      const specimen: Specimen = {
        id,
        position: { x: 0, y: 0 },
        isLiving: false,
        cellCount: 'none',
        cellType: 'none',
        lifeFunctions: [],
        bounds: new Phaser.Geom.Rectangle(-size / 2, -size / 2, size, size),
      };

      return {
        id,
        templateId,
        specimen,
        container,
        classified: false,
        correct: false,
      };
    }
  }

  /**
   * Get cell type from organism template
   */
  private getCellTypeFromTemplate(template: OrganismTemplate): Specimen['cellType'] {
    const cellType = template.cellConfig.cellType;
    if (cellType === 'bacteria') return 'prokaryotic';
    if (cellType === 'plant') return 'eukaryotic-plant';
    return 'eukaryotic';
  }

  /**
   * Create UI elements
   */
  private createUI(): void {
    const { width, height } = this.cameras.main;

    // Microscope HUD (right side)
    this.microscopeHUD = new MicroscopeHUD(this, this.microscopeSystem, {
      x: width - 50,
      y: height / 2 - 100,
    });

    // Scanner panel (bottom left) - listens to events automatically
    this.scannerPanel = new ScannerPanel(this, this.scannerSystem, {
      x: 20,
      y: height - 180,
    });

    // Evidence cards (left side)
    this.evidenceCards = new EvidenceCards(this, {
      x: 20,
      y: 80,
      maxCards: 5,
    });

    // Classification bins (bottom center)
    this.classificationBins = new ClassificationBins(this, this.classificationSystem, {
      x: width / 2,
      y: height - 100,
    });

    // Progress indicator (top center)
    this.createProgressIndicator();

    // Accuracy display (top right)
    this.createAccuracyDisplay();

    // Level title
    this.createLevelTitle();
  }

  /**
   * Create progress indicator
   */
  private createProgressIndicator(): void {
    const { width } = this.cameras.main;

    this.progressIndicator = this.add.container(width / 2, 30);
    this.progressIndicator.setDepth(1000);

    // Background
    const bg = this.add.graphics();
    bg.fillStyle(0x0a1628, 0.85);
    bg.fillRoundedRect(-100, -15, 200, 30, 8);
    bg.lineStyle(1, 0x4299e1, 0.5);
    bg.strokeRoundedRect(-100, -15, 200, 30, 8);

    // Text (will be updated)
    const text = this.add.text(0, 0, '', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '14px',
      color: '#e2e8f0',
    }).setOrigin(0.5);
    text.setData('type', 'progressText');

    this.progressIndicator.add([bg, text]);
    this.updateProgressIndicator();
  }

  /**
   * Create accuracy display
   */
  private createAccuracyDisplay(): void {
    const { width } = this.cameras.main;

    this.accuracyText = this.add.text(width - 120, 70, 'Accuracy: --', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '14px',
      color: '#90cdf4',
    });
    this.accuracyText.setOrigin(0.5);
    this.accuracyText.setDepth(1000);
  }

  /**
   * Create level title display
   */
  private createLevelTitle(): void {
    const chapter = getChapterForLevel(this.levelId);
    const chapterTitle = chapter?.title ?? 'Unknown Chapter';

    const title = this.add.text(20, this.cameras.main.height - 30, `${chapterTitle} - ${this.level.name}`, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '14px',
      color: '#a0aec0',
    });
    title.setDepth(1000);
  }

  /**
   * Update progress indicator
   */
  private updateProgressIndicator(): void {
    const total = this.specimenQueue.length;
    const current = this.currentSpecimenIndex + 1;

    const text = this.progressIndicator.list.find(
      child => child instanceof Phaser.GameObjects.Text && child.getData('type') === 'progressText'
    ) as Phaser.GameObjects.Text | undefined;

    if (text) {
      text.setText(`Specimen ${Math.min(current, total)} of ${total}`);
    }
  }

  /**
   * Update accuracy display
   */
  private updateAccuracyDisplay(): void {
    if (this.totalAttempts === 0) {
      this.accuracyText.setText('Accuracy: --');
      return;
    }

    const accuracy = Math.round((this.correctCount / this.totalAttempts) * 100);
    this.accuracyText.setText(`Accuracy: ${accuracy}%`);

    // Color based on accuracy
    if (accuracy >= 80) {
      this.accuracyText.setColor('#68d391');
    } else if (accuracy >= 60) {
      this.accuracyText.setColor('#f6e05e');
    } else {
      this.accuracyText.setColor('#fc8181');
    }
  }

  /**
   * Initialize objectives from level data
   */
  private initializeObjectives(): void {
    this.objectives = this.level.objectives.map(objective => ({
      objective,
      current: 0,
      target: objective.accuracy ?? 0.8,
      complete: false,
    }));
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    // Scanner events
    this.events.on('scanner:complete', this.handleScanComplete, this);
    this.events.on('evidence:collected', this.handleEvidenceCollected, this);

    // Classification events
    this.events.on('classification:correct', this.handleCorrectClassification, this);
    this.events.on('classification:incorrect', this.handleIncorrectClassification, this);

    // Keyboard shortcuts
    this.input.keyboard?.on('keydown-SPACE', () => this.scannerSystem.activate());
    this.input.keyboard?.on('keydown-ESC', () => this.pauseGame());
  }

  /**
   * Show the next specimen
   */
  private showNextSpecimen(): void {
    // Hide current specimen if any
    if (this.currentEntry) {
      this.currentEntry.container.setVisible(false);
      this.scannerSystem.unregisterSpecimen(this.currentEntry.specimen.id);
    }

    // Check if there are more specimens
    if (this.currentSpecimenIndex >= this.specimenQueue.length) {
      this.handleLevelComplete();
      return;
    }

    // Get next specimen
    this.currentEntry = this.specimenQueue[this.currentSpecimenIndex];
    this.currentEntry.container.setVisible(true);
    this.currentEntry.container.setPosition(0, 0);

    // Register with scanner
    this.currentEntry.specimen.position = { x: this.cameras.main.width / 2, y: this.cameras.main.height / 2 - 50 };
    this.currentEntry.specimen.bounds = new Phaser.Geom.Rectangle(
      this.cameras.main.width / 2 - 100,
      this.cameras.main.height / 2 - 150,
      200,
      200
    );
    this.scannerSystem.registerSpecimen(this.currentEntry.specimen);

    // Clear previous evidence
    this.classificationSystem.reset();
    this.collectedEvidence = [];
    this.evidenceCards.clearCards();

    // Update progress
    this.updateProgressIndicator();

    // Activate scanner
    this.scannerSystem.activate();

    // Show instruction if first specimen
    if (this.currentSpecimenIndex === 0) {
      this.showInstruction('Scan the specimen to collect evidence, then classify it.');
    }
  }

  /**
   * Show an instruction message
   */
  private showInstruction(message: string): void {
    const { width, height } = this.cameras.main;

    const instruction = this.add.text(width / 2, height / 2 + 200, message, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '14px',
      color: '#90cdf4',
      backgroundColor: '#0a1628',
      padding: { x: 15, y: 10 },
    }).setOrigin(0.5);
    instruction.setDepth(1000);

    this.tweens.add({
      targets: instruction,
      alpha: 0,
      delay: 3000,
      duration: 1000,
      onComplete: () => instruction.destroy(),
    });
  }

  /**
   * Handle scan completion
   */
  private handleScanComplete(event: ScanCompleteEvent): void {
    // Set specimen for classification
    this.classificationSystem.setCurrentSpecimen(event.specimen);

    // Play completion sound
    this.events.emit('sound:scan_complete');
  }

  /**
   * Handle evidence collection
   */
  private handleEvidenceCollected(evidence: EvidenceCard): void {
    if (!this.collectedEvidence.find(e => e.id === evidence.id)) {
      this.collectedEvidence.push(evidence);
      this.evidenceCards.addCard(evidence, true);
    }
  }

  /**
   * Handle correct classification
   */
  private handleCorrectClassification(_event: ClassificationResultEvent): void {
    if (!this.currentEntry) return;

    this.currentEntry.classified = true;
    this.currentEntry.correct = true;
    this.correctCount++;
    this.totalAttempts++;

    this.updateAccuracyDisplay();

    // Play correct sound
    this.events.emit('sound:correct');

    // Show feedback and move to next
    this.time.delayedCall(1500, () => {
      this.currentSpecimenIndex++;
      this.showNextSpecimen();
    });
  }

  /**
   * Handle incorrect classification
   */
  private handleIncorrectClassification(_event: ClassificationResultEvent): void {
    if (!this.currentEntry) return;

    this.currentEntry.classified = true;
    this.currentEntry.correct = false;
    this.totalAttempts++;

    this.updateAccuracyDisplay();

    // Play incorrect sound
    this.events.emit('sound:incorrect');

    // Allow retry or move on
    this.time.delayedCall(2000, () => {
      this.currentSpecimenIndex++;
      this.showNextSpecimen();
    });
  }

  /**
   * Check if level objectives are complete
   */
  private checkObjectives(): boolean {
    const accuracy = this.totalAttempts > 0 ? this.correctCount / this.totalAttempts : 0;

    for (const progress of this.objectives) {
      if (progress.objective.type === 'classify_correctly') {
        progress.current = accuracy;
        progress.complete = accuracy >= (progress.objective.accuracy ?? 0.8);
      } else if (progress.objective.type === 'sort_unicellular_multicellular') {
        progress.current = accuracy;
        progress.complete = accuracy >= (progress.objective.accuracy ?? 0.9);
      }
    }

    return this.objectives.every(o => o.complete);
  }

  /**
   * Handle level completion
   */
  private handleLevelComplete(): void {
    const objectivesComplete = this.checkObjectives();
    const accuracy = this.totalAttempts > 0 ? Math.round((this.correctCount / this.totalAttempts) * 100) : 0;

    // Mark level as complete if objectives are met
    if (objectivesComplete) {
      markLevelComplete(this.levelId);
    }

    this.events.emit('level:complete', {
      levelId: this.levelId,
      accuracy,
      correct: this.correctCount,
      total: this.totalAttempts,
      objectivesComplete,
    });

    this.showCompletionOverlay(objectivesComplete, accuracy);
  }

  /**
   * Show level completion overlay
   */
  private showCompletionOverlay(success: boolean, accuracy: number): void {
    const { width, height } = this.cameras.main;

    this.completionOverlay = this.add.container(width / 2, height / 2);
    this.completionOverlay.setDepth(2000);

    // Background
    const bg = this.add.graphics();
    bg.fillStyle(0x000000, 0.7);
    bg.fillRect(-width / 2, -height / 2, width, height);

    // Panel
    const panel = this.add.graphics();
    panel.fillStyle(0x0a1628, 0.95);
    panel.fillRoundedRect(-220, -180, 440, 360, 15);

    const borderColor = success ? 0x68d391 : 0xf6ad55;
    panel.lineStyle(2, borderColor, 0.9);
    panel.strokeRoundedRect(-220, -180, 440, 360, 15);

    // Title
    const titleText = success ? 'CHALLENGE COMPLETE!' : 'CHALLENGE FINISHED';
    const titleColor = success ? '#68d391' : '#f6ad55';
    const title = this.add.text(0, -130, titleText, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '26px',
      color: titleColor,
    }).setOrigin(0.5);

    // Stars based on accuracy
    const stars = this.getStars(accuracy);
    const starsText = this.add.text(0, -80, stars, {
      fontSize: '36px',
    }).setOrigin(0.5);

    // Stats
    const stats = [
      `Accuracy: ${accuracy}%`,
      `Correct: ${this.correctCount}/${this.totalAttempts}`,
    ];

    stats.forEach((stat, i) => {
      const statText = this.add.text(0, -20 + i * 35, stat, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#e2e8f0',
      }).setOrigin(0.5);
      this.completionOverlay!.add(statText);
    });

    // Objective result
    const objectiveText = success
      ? '✓ Classification objective met!'
      : '✗ Try again to improve your accuracy';
    const objResult = this.add.text(0, 70, objectiveText, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '14px',
      color: success ? '#68d391' : '#fc8181',
    }).setOrigin(0.5);

    // Buttons
    const retryBtn = this.createButton(-70, 130, 'RETRY', () => {
      this.completionOverlay?.destroy();
      this.scene.restart({ levelId: this.levelId });
    });

    const continueBtn = this.createButton(70, 130, success ? 'CONTINUE' : 'EXIT', () => {
      this.cleanup();
      this.scene.start('MenuScene');
    });

    this.completionOverlay.add([bg, panel, title, starsText, objResult, retryBtn, continueBtn]);
  }

  /**
   * Get star rating based on accuracy
   */
  private getStars(accuracy: number): string {
    if (accuracy >= 90) return '⭐⭐⭐';
    if (accuracy >= 70) return '⭐⭐☆';
    if (accuracy >= 50) return '⭐☆☆';
    return '☆☆☆';
  }

  /**
   * Create a button
   */
  private createButton(x: number, y: number, text: string, callback: () => void): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    const bg = this.add.graphics();
    bg.fillStyle(0x2b6cb0, 1);
    bg.fillRoundedRect(-60, -18, 120, 36, 8);

    const label = this.add.text(0, 0, text, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '14px',
      color: '#ffffff',
    }).setOrigin(0.5);

    container.add([bg, label]);
    container.setSize(120, 36);
    container.setInteractive({ useHandCursor: true });

    container.on('pointerdown', callback);
    container.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(0x3182ce, 1);
      bg.fillRoundedRect(-60, -18, 120, 36, 8);
    });
    container.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(0x2b6cb0, 1);
      bg.fillRoundedRect(-60, -18, 120, 36, 8);
    });

    return container;
  }

  /**
   * Pause game
   */
  private pauseGame(): void {
    this.events.emit('game:pause');
    this.scene.pause();
  }

  /**
   * Get current accuracy
   */
  public getAccuracy(): number {
    return this.totalAttempts > 0 ? this.correctCount / this.totalAttempts : 0;
  }

  /**
   * Get progress
   */
  public getProgress(): { current: number; total: number; accuracy: number } {
    return {
      current: this.currentSpecimenIndex,
      total: this.specimenQueue.length,
      accuracy: this.getAccuracy(),
    };
  }

  /**
   * Clean up resources
   */
  private cleanup(): void {
    this.microscopeSystem.destroy();
    this.scannerSystem.destroy();
    this.classificationSystem.destroy();
    this.animationSystem.destroy();
    this.microscopeHUD.destroy();
    this.scannerPanel.destroy();
    this.evidenceCards.destroy();
    this.classificationBins.destroy();

    this.events.off('scanner:complete', this.handleScanComplete, this);
    this.events.off('evidence:collected', this.handleEvidenceCollected, this);
    this.events.off('classification:correct', this.handleCorrectClassification, this);
    this.events.off('classification:incorrect', this.handleIncorrectClassification, this);
  }
}
